import { ref } from "vue";
import { api } from "../api/client";
import { money } from "../api/costs";
import { locale, t } from "../i18n";

/**
 * AI is charged in dollars. Every screen shows the dollar amount first and, beside it, its value in
 * reais at today's dollar: the latest PTAX quote of the Banco Central, which the API keeps.
 */
export interface UsdBrl {
  rate: number;
  /** The PTAX bulletin's day, YYYY-MM-DD. */
  day: string;
}

export const usdBrl = ref<UsdBrl | null>(null);
let loading: Promise<void> | null = null;

/** Loads today's dollar once per page; screens that already got it from the API can pass it. */
export function loadUsdBrl(slug: string): Promise<void> {
  loading ??= api<{ fx: UsdBrl | null }>(`/v1/c/${slug}/fx/usd-brl`)
    .then((r) => {
      usdBrl.value = r.fx;
    })
    .catch(() => {
      loading = null;
    });
  return loading;
}

/** Reais for dollars (micros) at a rate, or null without one. */
export function brlMicros(
  usdMicros: number,
  fx: UsdBrl | null | undefined = usdBrl.value,
): number | null {
  return fx ? Math.round(usdMicros * fx.rate) : null;
}

/** "≈ R$ 64,47 hoje"; small amounts keep four decimals. Empty without a rate. */
export function brlToday(
  usdMicros: number,
  fx: UsdBrl | null | undefined = usdBrl.value,
): string {
  const brl = brlMicros(usdMicros, fx);
  if (brl === null) return "";
  return t("fx.approx_today", {
    value: money(brl, "BRL", Math.abs(brl) < 1_000_000),
  });
}

function fxParts(fx: UsdBrl) {
  return {
    rate: new Intl.NumberFormat(locale.value, {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 4,
    }).format(fx.rate),
    day: new Date(fx.day + "T12:00:00Z").toLocaleDateString(locale.value, {
      day: "2-digit",
      month: "2-digit",
      timeZone: "UTC",
    }),
  };
}

/** "Dólar de hoje: US$ 1 = R$ 5,1991 (PTAX de 25/09)", for a tooltip. */
export function fxRateText(
  fx: UsdBrl | null | undefined = usdBrl.value,
): string {
  return fx ? t("fx.rate", fxParts(fx)) : t("fx.no_rate");
}

/** The note under totals that mix Meta's reais with AI's dollars. */
export function fxAiNote(fx: UsdBrl | null | undefined = usdBrl.value): string {
  return fx ? t("fx.ai_note", fxParts(fx)) : t("fx.no_rate");
}
